import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";

import { useTabsStore } from "@/stores/react/tabsStore";

interface DetailNavigationState {
    returnTo?: string;
}

/** 只接受站内绝对路径，避免把路由 state 当作外部跳转地址使用。 */
function isInternalPath(value: unknown): value is string {
    return typeof value === "string" && value.startsWith("/") && !value.startsWith("//");
}

/**
 * 统一详情页返回行为：优先显式来源，其次页签首次来源，直达页面最后回到模块列表。
 */
export function useDetailBack(fallbackPath: string): () => void {
    const location = useLocation();
    const navigate = useNavigate();
    const setActivePath = useTabsStore(state => state.setActivePath);
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const tabReturnTo = useTabsStore(state => state.tabs.find(tab => tab.path === currentPath)?.returnTo);
    const stateReturnTo = (location.state as DetailNavigationState | null)?.returnTo;
    const returnTo = isInternalPath(stateReturnTo)
        ? stateReturnTo
        : isInternalPath(tabReturnTo)
          ? tabReturnTo
          : undefined;

    return useCallback(() => {
        const targetPath = returnTo && returnTo !== currentPath ? returnTo : fallbackPath;
        // 先切换页签活动路径，避免目标详情页重新激活时把当前页误记为新的来源。
        setActivePath(targetPath);
        void navigate(targetPath);
    }, [currentPath, fallbackPath, navigate, returnTo, setActivePath]);
}

export default useDetailBack;
